import type { DeviceMemory } from '#src/groups/device_memory/device_memory.ts';
import { DeviceMemoryRules } from '#src/groups/device_memory/device_memory.ts';
import type { UseDeviceMemoryResult } from '#src/groups/device_memory/use_device_memory.ts';
import { useDeviceMemory } from '#src/groups/device_memory/use_device_memory.ts';
import { useGetGroup } from '#src/groups/get_group/use_get_group.ts';
import { ChooseNameCard } from '#src/pages/group/choose_name_card/choose_name_card.tsx';
import { ConfirmNameCard } from '#src/pages/group/confirm_name_card/confirm_name_card.tsx';
import { DeletedGroupCard } from '#src/pages/group/deleted_group_card/deleted_group_card.tsx';
import { LeaveNameCard } from '#src/pages/group/leave_name_card/leave_name_card.tsx';
import { ParticipantDashboard } from '#src/pages/group/participant_dashboard.tsx';
import { useServices } from '#src/services/services.ts';
import { NotFoundPage } from '#src/ui/error/not_found_page.tsx';
import { UnknownErrorPage } from '#src/ui/error/unknown_error_page.tsx';
import { PageLayout } from '#src/ui/layout/page_layout.tsx';
import { LoadingPage } from '#src/ui/loading/loading_page.tsx';
import { getCreateGroupHref, getGroupHref } from '#src/ui/navigation/navigation.ts';
import { useDocumentTitle } from '#src/ui/navigation/use_document_title.ts';
import type { Group } from '@packages/contracts/group';
import { GetGroupErrorCodes, GetGroupSuccessKinds } from '@packages/contracts/group';
import type { EnumOf } from '@packages/primitives/enum';
import { exists } from '@packages/primitives/exists';
import { invariant } from '@packages/primitives/invariant';
import { UnreachableError } from '@packages/primitives/unreachable_error';
import { useState } from 'react';
import { useParams } from 'react-router';

const PAGE_TITLE = 'Draw Your Secret Santa Match | draw.name';

export const GroupPage = () => {
  useDocumentTitle(PAGE_TITLE);

  const { groupId } = useParams();

  if (!groupId) {
    return <NotFoundPage />;
  }

  return <GroupPageContent key={groupId} groupId={groupId} />;
};

const GroupPageContent = ({ groupId }: { groupId: string }) => {
  const { groupClient, deviceMemoryStore } = useServices();
  const getGroup = useGetGroup(groupClient, groupId);
  const deviceMemory = useDeviceMemory(deviceMemoryStore, groupId);
  const [view, setView] = useState<GroupView>(() => getInitialView(deviceMemory.memory));

  if (getGroup.httpError && !getGroup.result) {
    return <UnknownErrorPage />;
  }

  if (getGroup.isPending) {
    return <LoadingPage />;
  }

  invariant(getGroup.result, 'Group result must be defined');

  if (!getGroup.result.success) {
    switch (getGroup.result.code) {
      case GetGroupErrorCodes.INVALID_INPUT:
      case GetGroupErrorCodes.NOT_FOUND:
        return <NotFoundPage />;
      default:
        throw new UnreachableError(getGroup.result.code);
    }
  }

  if (getGroup.result.kind === GetGroupSuccessKinds.DELETED) {
    return <DeletedGroup groupId={groupId} />;
  }

  return (
    <ActiveGroup
      group={getGroup.result.group}
      deviceMemory={deviceMemory}
      view={view}
      onViewChange={setView}
    />
  );
};

const ActiveGroup = ({
  group,
  deviceMemory,
  view,
  onViewChange,
}: {
  group: Group;
  deviceMemory: UseDeviceMemoryResult;
  view: GroupView;
  onViewChange: (view: GroupView) => void;
}) => {
  const { captureEvent } = useServices();
  const { memory } = deviceMemory;
  const resolved = resolveView(view, memory, group);

  const chooseAnotherName = () => onViewChange({ kind: GroupViewKinds.CHOOSE });

  switch (resolved.kind) {
    case GroupViewKinds.CHOOSE:
      return (
        <PageLayout scrolling="viewport">
          <ChooseNameCard
            groupName={group.name}
            participants={group.participants}
            onChooseParticipant={participantId => {
              // The last match revealed on this device opens directly, with no wait or draw.
              if (DeviceMemoryRules.isLastRevealed(memory, participantId)) {
                deviceMemory.select(participantId);
                onViewChange({ kind: GroupViewKinds.DASHBOARD, participantId });
                return;
              }
              onViewChange({ kind: GroupViewKinds.CONFIRM, participantId });
            }}
          />
        </PageLayout>
      );
    case GroupViewKinds.CONFIRM:
      return (
        <PageLayout>
          <ConfirmNameCard
            // Remount per name so each confirmation starts its own wait.
            key={resolved.participant.id}
            captureEvent={captureEvent}
            participant={resolved.participant}
            deviceMemory={memory}
            onConfirm={() => {
              deviceMemory.select(resolved.participant.id);
              onViewChange({
                kind: GroupViewKinds.DASHBOARD,
                participantId: resolved.participant.id,
              });
            }}
            onCancel={chooseAnotherName}
          />
        </PageLayout>
      );
    case GroupViewKinds.DASHBOARD:
      return (
        <ParticipantDashboard
          groupId={group.id}
          participantId={resolved.participant.id}
          deviceMemory={deviceMemory}
          onLeaveClick={() =>
            onViewChange({ kind: GroupViewKinds.LEAVE, participantId: resolved.participant.id })
          }
        />
      );
    case GroupViewKinds.LEAVE:
      return (
        <PageLayout>
          <LeaveNameCard
            captureEvent={captureEvent}
            participantName={resolved.participant.name}
            onLeave={() => {
              deviceMemory.forget();
              chooseAnotherName();
            }}
            onCancel={() =>
              onViewChange({
                kind: GroupViewKinds.DASHBOARD,
                participantId: resolved.participant.id,
              })
            }
          />
        </PageLayout>
      );
    default:
      throw new UnreachableError(resolved);
  }
};

const DeletedGroup = ({ groupId }: { groupId: string }) => {
  return (
    <PageLayout>
      <DeletedGroupCard
        groupUrl={getGroupHref({ groupId, absolute: true })}
        createGroupHref={getCreateGroupHref()}
      />
    </PageLayout>
  );
};

// View state

const GroupViewKinds = {
  CHOOSE: 'CHOOSE',
  CONFIRM: 'CONFIRM',
  DASHBOARD: 'DASHBOARD',
  LEAVE: 'LEAVE',
} as const;

type GroupViewKind = EnumOf<typeof GroupViewKinds>;

/** What this tab is showing. Device memory can still change underneath it from another tab. */
type GroupView =
  | { kind: typeof GroupViewKinds.CHOOSE }
  | { kind: Exclude<GroupViewKind, typeof GroupViewKinds.CHOOSE>; participantId: string };

type ResolvedGroupView =
  | { kind: typeof GroupViewKinds.CHOOSE }
  | {
      kind: Exclude<GroupViewKind, typeof GroupViewKinds.CHOOSE>;
      participant: Group['participants'][number];
    };

/** A remembered name returns straight to its dashboard, without confirming again. */
function getInitialView(memory: DeviceMemory): GroupView {
  return exists(memory.selectedParticipantId)
    ? { kind: GroupViewKinds.DASHBOARD, participantId: memory.selectedParticipantId }
    : { kind: GroupViewKinds.CHOOSE };
}

function resolveView(view: GroupView, memory: DeviceMemory, group: Group): ResolvedGroupView {
  if (view.kind === GroupViewKinds.CHOOSE) return view;

  // A remembered name that is no longer in the group starts over.
  const participant = group.participants.find(p => p.id === view.participantId);
  if (!exists(participant)) return { kind: GroupViewKinds.CHOOSE };

  // A stale tab for an older name returns to its confirmation rather than
  // gaining access under the name another tab confirmed since.
  if (view.kind !== GroupViewKinds.CONFIRM && memory.selectedParticipantId !== participant.id) {
    return { kind: GroupViewKinds.CONFIRM, participant };
  }

  return { kind: view.kind, participant };
}
